import React from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/ui';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { CONTACT_EMAIL, mailto } from '../lib/site';

/** Overarching FairCorp Privacy Policy. Data controller is Brennan Faires
 * (FairCorp is his trade name, not a separate legal entity). */
const Privacy: React.FC = () => {
  return (
    <section className="page-shell page-section-major">
      <div className="page-inner">
      <Reveal dir="up">
        <Card className="shadow-none overflow-hidden">
          <CardContent className="p-8 sm:p-10">
            <p className="text-primary-400 text-xs font-medium tracking-[0.24em] uppercase mb-4">
              Privacy Policy
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              FairCorp &mdash; Privacy Policy
            </h1>
            <p className="text-secondary-500 text-sm mb-6">
              Effective date: October 5, 2026 &middot; Last updated: October 5, 2026
            </p>
            <div className="text-secondary-300 text-[0.95rem] leading-loose space-y-4">
              <p>
                This Privacy Policy explains what information{' '}
                <strong className="text-white">Brennan Faires</strong>, operating as FairCorp,
                collects through the FairCorp websites and services &mdash; including the FairCorp
                hub (fair-corp.com), the hosting directory, the ToolBox storefront, and websites
                hosted on FairCorp infrastructure &mdash; and how we use, store, protect, and share
                it. FairCorp is a trade name for software and services published by Brennan Faires,
                an individual, and is not a separate legal entity; Brennan Faires is the sole legal
                counterparty and data controller. Individual products may carry their own
                product-specific privacy notices, which supplement this policy for that product.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">1. What we collect</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong className="text-white">Account information</strong> you provide (email, optional display name, password hash &mdash; never plaintext &mdash; plus role and account metadata).</li>
                <li><strong className="text-white">Contact information</strong> you submit through contact, ticket, application, or partnership forms (name, email, message content).</li>
                <li><strong className="text-white">Technical information:</strong> standard server and error logs (IP address, timestamps, requested page, user agent) kept for a limited time for security and reliability.</li>
                <li><strong className="text-white">Visit counts:</strong> some pages record an aggregate page-view count so directory listings stay accurate. These are statistics, not advertising profiles.</li>
              </ul>
              <p>
                We do not collect precise geolocation, cross-site browsing history, keystrokes, or
                advertising identifiers, and the Sites contain no third-party advertising or
                analytics SDKs.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">2. How we use it</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Operate, maintain, and secure the Sites and the services you request.</li>
                <li>Authenticate you and keep accounts and data secure.</li>
                <li>Respond to your requests and provide support.</li>
                <li>Monitor reliability, performance, and security.</li>
                <li>Comply with legal obligations and enforce our <Link to="/tos" className="text-primary-400 hover:text-primary-300 underline underline-offset-4">Terms of Service</Link>.</li>
              </ul>
              <p>We do not use your information for advertising.</p>
              <h2 className="text-white text-lg font-bold pt-2">3. Cookies</h2>
              <p>
                Account-gated surfaces use a secure, HTTP-only session cookie to keep you signed
                in. We do not use advertising or cross-site tracking cookies, and we do not use
                third-party analytics.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">4. Sharing</h2>
              <p>We do not sell or rent your personal information. We share it only:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>with contractors or service providers operating the Sites on Brennan Faires&rsquo;s behalf, bound to protect it consistently with this policy;</li>
                <li>where required by law, legal process, or governmental request, or to protect rights, property, or safety; or</li>
                <li>in connection with a sale or transfer of the service assets, subject to this policy and legally required notice or consent.</li>
              </ul>
              <h2 className="text-white text-lg font-bold pt-2">5. Retention and security</h2>
              <p>
                We keep information only as long as needed for the purposes above. Traffic to the
                Sites is encrypted with TLS (HTTPS), and access to personal data is restricted on a
                need-to-know basis. No method is perfectly secure; if you believe your data has been
                compromised, contact us immediately at{' '}
                <a href={mailto()} className="text-primary-400 hover:text-primary-300 underline underline-offset-4">
                  {CONTACT_EMAIL}
                </a>.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">6. Your rights and choices</h2>
              <p>
                To request access, correction, or deletion of your personal information, email{' '}
                <a href={mailto()} className="text-primary-400 hover:text-primary-300 underline underline-offset-4">
                  {CONTACT_EMAIL}
                </a>{' '}
                from the address on the account; we typically respond within 30 days. U.S. state
                privacy laws (including the CCPA/CPRA, the Colorado Privacy Act, and similar laws)
                may grant additional rights, including portability and non-discrimination, which we
                honor. We do not sell or share personal information and do not engage in targeted
                advertising, so there is nothing to opt out of.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">7. Children and international users</h2>
              <p>
                The Sites are not directed to children under 13. The Sites are operated from the
                United States; accessing them from elsewhere means your information is processed and
                stored in the United States.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">8. Changes and contact</h2>
              <p>
                We may update this policy, revising the &ldquo;Last updated&rdquo; date above, with
                reasonable notice of material changes. Continued use after changes take effect means
                acceptance. Questions: Brennan Faires (operating as FairCorp),{' '}
                <a href={mailto()} className="text-primary-400 hover:text-primary-300 underline underline-offset-4">
                  {CONTACT_EMAIL}
                </a>.
              </p>
            </div>
            <Separator className="my-6" />
            <Link to="/" className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-medium">
              Back to Home
            </Link>
          </CardContent>
        </Card>
      </Reveal>
      </div>
    </section>
  );
};

export default Privacy;
